import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import RecurrencePicker from './RecurrencePicker';
import { useEventPanel } from '../contexts/EventPanelContext';
import {
  eventFormSchema,
  type EventFormValues,
  parseTags,
} from '../schemas/eventSchema';

/**
 * Edit-only event form used inside EventPanel.
 *
 * Reads all data and actions from EventPanelContext via useEventPanel().
 * Uses React Hook Form with FormProvider so child components like RecurrencePicker
 * can access form state via useFormContext.
 */
export default function EventForm() {
  const {
    mode,
    defaultValues,
    accentColor,
    isPending,
    recurrenceDisabled,
    extraContent,
    onSubmit,
    onClose,
    exitEditMode,
    isKocal,
    userRole,
    calendarName,
    canEdit,
  } = useEventPanel();

  const methods = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const allDay = watch('allDay');
  const startAt = watch('startAt');
  const tags = watch('tags');
  const asKocal = watch('asKocal');
  const address = watch('address');
  const phone = watch('phone');

  // Progressive disclosure visibility state
  const [showAddress, setShowAddress] = useState(
    Boolean(defaultValues.address)
  );
  const [showPhone, setShowPhone] = useState(
    Boolean(defaultValues.phone)
  );
  const [showTags, setShowTags] = useState(
    (defaultValues.tags?.length ?? 0) > 0
  );
  const [tagInput, setTagInput] = useState('');

  // Show end date picker for timed events when editing an event that spans multiple days
  const [showEndDate, setShowEndDate] = useState(() => {
    if (defaultValues.allDay) return false;
    const startDate = defaultValues.startAt.slice(0, 10);
    const endDate = defaultValues.endAt.slice(0, 10);
    return startDate !== endDate;
  });

  const pad = (n: number) => String(n).padStart(2, '0');

  function formatLocalDatetime(d: Date): string {
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  }

  function handleCancel() {
    if (mode === 'create') {
      onClose();
    } else {
      exitEditMode();
    }
  }

  // Show kocal toggle when creating on a kocal and user is admin
  const showKocalToggle =
    mode === 'create' &&
    isKocal &&
    (userRole === 'owner' || userRole === 'manager');

  // Update recurrence event date when start changes
  const recurrenceEventDate = new Date(startAt);

  function addTag(raw: string) {
    const newTags = parseTags(raw);
    if (newTags.length === 0) return;
    const merged = [
      ...tags.filter(t => !newTags.includes(t)),
      ...newTags,
    ];
    setValue('tags', merged);
    setTagInput('');
  }

  function removeTag(tag: string) {
    setValue(
      'tags',
      tags.filter(t => t !== tag)
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col h-full"
      >
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Kocal perspective toggle (admin on kocal only) */}
          {showKocalToggle && (
            <div className="px-3 py-2 bg-paper-100 rounded-sm">
              <label className="block font-mono text-xs text-ink-400 mb-1.5">
                Add event as:
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setValue('asKocal', false)}
                  className={[
                    'flex-1 px-2 py-1.5 rounded-sm text-xs font-mono transition-colors border',
                    !asKocal
                      ? 'text-white shadow-sm'
                      : 'bg-paper-200 text-ink-600 border-paper-300 hover:bg-paper-300',
                  ].join(' ')}
                  style={
                    !asKocal
                      ? {
                          backgroundColor: accentColor,
                          borderColor: accentColor,
                        }
                      : undefined
                  }
                >
                  Myself (contribute)
                </button>
                <button
                  type="button"
                  onClick={() => setValue('asKocal', true)}
                  className={[
                    'flex-1 px-2 py-1.5 rounded-sm text-xs font-mono transition-colors border',
                    asKocal
                      ? 'text-white shadow-sm'
                      : 'bg-paper-200 text-ink-600 border-paper-300 hover:bg-paper-300',
                  ].join(' ')}
                  style={
                    asKocal
                      ? {
                          backgroundColor: accentColor,
                          borderColor: accentColor,
                        }
                      : undefined
                  }
                >
                  {calendarName || 'Calendar'}
                </button>
              </div>
              <p className="mt-1.5 font-mono text-xs text-ink-300">
                {!asKocal
                  ? 'Event goes on your personal calendar and appears here'
                  : 'Event goes directly on this calendar'}
              </p>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              autoFocus
              maxLength={255}
              className="input-field"
              placeholder="Event title"
              {...register('title')}
            />
            {errors.title && (
              <p className="mt-1 font-mono text-xs text-red-500">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* All-day toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
              All day
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={allDay}
              onClick={() => {
                const next = !allDay;
                setValue('allDay', next);
                if (next) {
                  // When switching to all-day, normalize both start and end to
                  // midnight on the selected date. endAt is inclusive, so a
                  // same-day all-day event has identical start/end values.
                  const currentStart = watch('startAt');
                  const datePart = currentStart.slice(
                    0,
                    10
                  );
                  setValue('startAt', datePart + 'T00:00');
                  setValue('endAt', datePart + 'T00:00');
                }
              }}
              className={[
                'relative w-9 h-5 rounded-full transition-colors',
                allDay ? 'bg-ink-900' : 'bg-paper-300',
              ].join(' ')}
            >
              <span
                className={[
                  'absolute top-0.5 w-4 h-4 rounded-full bg-paper-50 shadow transition-all',
                  allDay ? 'left-4' : 'left-0.5',
                ].join(' ')}
              />
            </button>
          </label>

          {/* Start */}
          <div>
            <label
              htmlFor="event-start"
              className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest"
            >
              Start
            </label>
            <input
              id="event-start"
              type={allDay ? 'date' : 'datetime-local'}
              className="input-field"
              value={
                allDay
                  ? (() => {
                      const startAtValue = watch('startAt');
                      return startAtValue &&
                        startAtValue.length >= 10
                        ? startAtValue.slice(0, 10)
                        : new Date()
                            .toISOString()
                            .slice(0, 10);
                    })()
                  : watch('startAt')
              }
              onChange={e => {
                const currentStart = watch('startAt');
                const currentEnd = watch('endAt');
                const newStartRaw = allDay
                  ? e.target.value + 'T00:00'
                  : e.target.value;
                setValue('startAt', newStartRaw);

                if (!allDay) {
                  const oldStartMs = new Date(
                    currentStart
                  ).getTime();
                  const endMs = new Date(
                    currentEnd
                  ).getTime();
                  const duration = endMs - oldStartMs;
                  const newStartMs = new Date(
                    newStartRaw
                  ).getTime();
                  if (newStartMs >= endMs) {
                    const newEnd = new Date(
                      newStartMs + duration
                    );
                    const newEndStr =
                      formatLocalDatetime(newEnd);
                    setValue('endAt', newEndStr);
                    // Keep end date visible if the bumped end is on a different day
                    if (
                      newEndStr.slice(0, 10) !==
                      newStartRaw.slice(0, 10)
                    ) {
                      setShowEndDate(true);
                    }
                  } else if (!showEndDate) {
                    // Keep end date locked to new start date when end date is hidden
                    const endTime = currentEnd.slice(11);
                    setValue(
                      'endAt',
                      newStartRaw.slice(0, 10) +
                        'T' +
                        endTime
                    );
                  }
                }
              }}
            />
          </div>

          {/* End */}
          <div>
            <label
              htmlFor="event-end"
              className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest"
            >
              End
            </label>
            {allDay ? (
              <input
                id="event-end"
                type="date"
                className="input-field"
                value={(() => {
                  const endAtValue = watch('endAt');
                  const startAtValue = watch('startAt');
                  const raw =
                    endAtValue && endAtValue.length >= 10
                      ? endAtValue
                      : startAtValue;
                  return raw.slice(0, 10);
                })()}
                onChange={e => {
                  setValue(
                    'endAt',
                    e.target.value + 'T00:00'
                  );
                }}
              />
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  {showEndDate && (
                    <input
                      id="event-end-date"
                      type="date"
                      className="input-field flex-1"
                      value={watch('endAt').slice(0, 10)}
                      onChange={e => {
                        const endTime =
                          watch('endAt').slice(11);
                        setValue(
                          'endAt',
                          e.target.value + 'T' + endTime
                        );
                      }}
                    />
                  )}
                  <input
                    id="event-end"
                    type="time"
                    className={[
                      'input-field',
                      showEndDate
                        ? 'w-32 shrink-0'
                        : 'flex-1',
                    ].join(' ')}
                    value={watch('endAt').slice(11, 16)}
                    onChange={e => {
                      const endDate = watch('endAt').slice(
                        0,
                        10
                      );
                      setValue(
                        'endAt',
                        endDate + 'T' + e.target.value
                      );
                    }}
                  />
                </div>
                {!showEndDate ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowEndDate(true);
                    }}
                    className="block font-mono text-xs text-ink-300 hover:text-ink-500 transition-colors text-left"
                  >
                    + different end date
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setShowEndDate(false);
                      // Snap end date back to start date
                      const startDate = watch(
                        'startAt'
                      ).slice(0, 10);
                      const endTime =
                        watch('endAt').slice(11);
                      setValue(
                        'endAt',
                        startDate + 'T' + endTime
                      );
                    }}
                    className="block font-mono text-xs text-ink-300 hover:text-ink-500 transition-colors text-left"
                  >
                    − same day
                  </button>
                )}
              </div>
            )}
            {errors.endAt && (
              <p className="mt-1 font-mono text-xs text-red-500">
                {errors.endAt.message}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
              Description
            </label>
            <textarea
              className="input-field resize-none"
              rows={4}
              maxLength={2000}
              placeholder="Optional notes"
              {...register('description')}
            />
          </div>

          {/* Recurrence — directly below description */}
          <div>
            <RecurrencePicker
              value={watch('recurrence')}
              onChange={pattern =>
                setValue('recurrence', pattern)
              }
              eventDate={recurrenceEventDate}
              disabled={recurrenceDisabled}
            />
          </div>

          {/* Address (progressive disclosure) */}
          {showAddress ? (
            <div>
              <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
                Address
              </label>
              <input
                type="text"
                className="input-field"
                maxLength={500}
                placeholder="123 Main St, City, State"
                value={address ?? ''}
                onChange={e =>
                  setValue('address', e.target.value)
                }
              />
              {address?.trim() && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(address.trim())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1 font-mono text-xs text-ink-300 hover:text-ink-500 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  map it
                </a>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddress(true)}
              className="block font-mono text-xs text-ink-300 hover:text-ink-500 transition-colors text-left"
            >
              + add address
            </button>
          )}

          {/* Phone (progressive disclosure) */}
          {showPhone ? (
            <div>
              <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
                Contact #
              </label>
              <input
                type="tel"
                className="input-field"
                maxLength={50}
                placeholder="(555) 555-5555"
                value={phone ?? ''}
                onChange={e =>
                  setValue('phone', e.target.value)
                }
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowPhone(true)}
              className="block font-mono text-xs text-ink-300 hover:text-ink-500 transition-colors text-left"
            >
              + add contact #
            </button>
          )}

          {/* Tags (progressive disclosure with individual chips) */}
          {showTags ? (
            <div>
              <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
                Tags
              </label>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-paper-200 font-mono text-xs text-ink-600"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-ink-400 hover:text-ink-700 leading-none"
                        aria-label={`Remove tag ${tag}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                className="input-field"
                placeholder="Add tag, press Enter or comma"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag(tagInput);
                  }
                }}
                onBlur={() => {
                  if (tagInput.trim()) addTag(tagInput);
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowTags(true)}
              className="block font-mono text-xs text-ink-300 hover:text-ink-500 transition-colors text-left"
            >
              + add tag
            </button>
          )}

          {extraContent && (
            <div className="space-y-2">{extraContent}</div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 pt-2 space-y-2 shrink-0 border-t border-paper-200">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-ghost flex-1"
            >
              Cancel
            </button>
            {canEdit && (
              <button
                type="submit"
                disabled={
                  isPending || !watch('title').trim()
                }
                className="btn-primary flex-1"
                style={{ backgroundColor: accentColor }}
              >
                {isPending
                  ? 'Saving…'
                  : mode === 'create'
                    ? 'Add event'
                    : 'Save'}
              </button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
